import React, { Component } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { JobService } from "@services";
import classNames from "classnames";
import { Modal, Loader, ReactSelect, ScrollDetect } from "../../../../../components";

import "./JobHistoryModal.scss";

const actions = [
	{ label: "Applications", value: 1 },
	{ label: "Assignments", value: 2 },
	{ label: "Call Offs", value: 3 },
	{ label: "Denials", value: 4 },
	{ label: "Posting", value: 5 },
	{ label: "Unposting", value: 6 },
	{ label: "Unassignments", value: 7 },
	{ label: "Withdrawals", value: 8 },
];

const mappedMetadata = {
	AssignmentRuleName: "Rule Name",
	OnlyGroupsCanSeeItNoDelay: "Only officer groups can see it without delay",
	DelayOfSpecificTime: "Delay of specific time",
	PostToAllIfNotFilled: "Post to all officers if not filled 48 hrs before the job start",
	AssignmentRuleType: "Rule type",
	FCFSLimit: "FCFS Limit # of jobs:",
	Order: "Order",
	SignUpPeriod: "Sign up period",
	TimeToConsider: "Time to consider",
	Limits: "Limits",
	NumberOfRounds: "Number of rounds",
	IsFcFsOptionSelected: "If the FCFS option is turned on",
	IsOpenWhileActiveSelected: "If the keep job open while active is turned on",
};

const JURISDICTION_SERVICE_DATE_METADATA_KEY = "Seniority Date at time of assignment";

class JobHistoryModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			jobHistoryList: [],
			jobHistoryItem: {},
			isLoading: false,
			activeTab: "job-log",
			actionFilter: undefined,
			officerFilter: undefined,
			positionFilter: undefined,
			sortAsc: true,
			applicantLog: [],
			applicantLogHasMore: false,
			applicantLogPageNumber: 1,
			filteredApplicantLog: [],
			filteredApplicantLogItem: {},
			officerList: [],
			positionList: [],
			actionList: [],
		};
	}

	componentDidUpdate(prevProps) {
		const { show, jobEventId } = this.props;
		if (prevProps.show !== show && show) {
			this.getJobHistory(jobEventId);
			this.getApplicantHistory();
		}
	}

	setUpFilters = data => {
		const { applicantLog, actionFilter, positionFilter, officerFilter } = this.state;
		const totalData = [...applicantLog, ...data];
		const posData = totalData.filter(
			(val, index, self) => index === self.findIndex(t => t.JobId === val.JobId),
		);
		const officerData = totalData.filter(
			(val, index, self) => index === self.findIndex(t => t.UserId === val.UserId),
		);
		const positions = posData.map(item => ({
			label: item.JobId ? item.JobName : "No positions",
			value: item.JobId ? item.JobId : -1,
		}));
		const officers = officerData.map(item => ({ label: item.UserName, value: item.UserId }));
		this.setState({
			officerList: officers,
			positionList: positions,
			actionList: actions,
			actionFilter,
			positionFilter,
			officerFilter,
		});
	};

	changeSort = () => {
		const { filteredApplicantLog, applicantLog } = this.state;
		const revFilteredApplicantLog = [...filteredApplicantLog].reverse();
		const revApplicantLog = [...applicantLog].reverse();
		this.setState(prevState => ({
			sortAsc: !prevState.sortAsc,
			filteredApplicantLog: revFilteredApplicantLog,
			applicantLog: revApplicantLog,
		}));
	};

	getApplicantHistory = () => {
		const { lang } = global;
		const { applicantLogPageNumber, positionFilter, officerFilter, actionFilter } = this.state;
		const { jobEventId } = this.props;

		this.setState({ isLoading: true });
		JobService.getApplicantsHistoryById(jobEventId, {
			pageNumber: applicantLogPageNumber,
			userId: officerFilter,
			jobId: positionFilter,
			action: actionFilter,
		})
			.then(({ data }) => {
				const { ItemList } = data;
				this.setUpFilters(ItemList);
				this.processLogs(data);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading applicants history"),
				});
				this.setState({
					isLoading: false,
				});
			});
	};

	processLogs = data => {
		const { ItemList, DisplayCount } = data;
		const { filteredApplicantLog } = this.state;

		const newData = [...filteredApplicantLog];
		const { timeZoneId, user } = this.props;
		const { filteredApplicantLogItem } = this.state;

		for (let i = 0; i < ItemList.length; i++) {
			const log = ItemList[i];
			const auxDate = this.findDate(ItemList[i].ApplicantLog);
			if (auxDate != null) {
				const newDate = utils.formatTime(
					new Date(`${auxDate}Z`),
					user.UserPreferences !== "useMilitaryTime",
					timeZoneId,
					true,
				);
				const newTime = utils.formatDate(`${auxDate}Z`, "en-US", timeZoneId, "numeric");
				log.ApplicantLog = log.ApplicantLog.replace(auxDate, `${newTime} ${newDate}`);
			}
			newData.push(log);
		}
		const applicantHistoryLogList = newData.map((log, order) => ({ ...log, order }));
		applicantHistoryLogList.forEach(log => {
			if (!filteredApplicantLogItem[log.order])
				filteredApplicantLogItem[log.order] = { showDetails: false };
		});
		this.setState(prevState => ({
			applicantLog: applicantHistoryLogList,
			filteredApplicantLog: applicantHistoryLogList,
			isLoading: false,
			applicantLogHasMore: ItemList.length >= DisplayCount,
			applicantLogPageNumber: prevState.applicantLogPageNumber + 1,
			filteredApplicantLogItem,
		}));
	};

	/**
	 * Retrieves a history of changes for the current job event
	 * @param {Number} jobId The ID number for the job event
	 */
	getJobHistory = jobId => {
		const { lang } = global;
		const { jobHistoryItem } = this.state;
		this.setState({ isLoading: true });
		JobService.getJobHistoryById(jobId)
			.then(({ data }) => {
				// add a unique id 'order' to each list item
				const jobHistoryList = data.map((job, order) => ({ ...job, order }));
				jobHistoryList.forEach(job => {
					if (!jobHistoryItem[job.order])
						jobHistoryItem[job.order] = { showDetails: false };
				});
				this.setState({
					jobHistoryList,
					jobHistoryItem,
					isLoading: false,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading job history"),
				});
				this.setState({ isLoading: false });
			});
	};

	handleOnClose = () => {
		const { onClose } = this.props;
		this.setState({
			jobHistoryList: [],
			jobHistoryItem: {},
			isLoading: false,
		});
		onClose();
	};

	toggleShowDetails = id =>
		this.setState(prevState => ({
			jobHistoryItem: {
				...prevState.jobHistoryItem,
				[id]: {
					...prevState.jobHistoryItem[id],
					showDetails: !prevState.jobHistoryItem[id].showDetails,
				},
			},
		}));

	toggleShowApplicantDetails = id =>
		this.setState(prevState => ({
			filteredApplicantLogItem: {
				...prevState.filteredApplicantLogItem,
				[id]: {
					...prevState.filteredApplicantLogItem[id],
					showDetails: !prevState.filteredApplicantLogItem[id].showDetails,
				},
			},
		}));

	/**
	 * Converts UTC time to local time when applicable
	 * @param {String} value The string value used for time comparison
	 * @returns {String} The value which will be displayed whether or not any conversion takes place
	 */

	findDate = value => {
		let result;
		// eslint-disable-next-line
		result = value.match(
			"[0-9]{4}([-/ .])[0-9]{2}[-/ .][0-9]{2}[T]{1}[0-9]{2}([:/ .])[0-9]{2}[:/ .][0-9]{2}",
		);
		return result ? result[0] : null;
	};

	formatJobHistoryValue = value => {
		const { timeZoneId, user } = this.props;
		let string;

		// Checks if the provided value is a date by getting its hours
		// if hours are retrieved convert the UTC time to local time
		if (new Date(value).getHours() !== 0 && !Number.isNaN(new Date(value).getHours())) {
			string = `${utils.formatDate(new Date(`${value} UTC`), "en-US", timeZoneId, "2-digit")}
			${utils.formatTime(new Date(`${value} UTC`), user.UserPreferences !== "useMilitaryTime", timeZoneId)}`;
		} else {
			string = value;
		}
		// Otherwise return the string as is
		return string;
	};

	changeTab = ({ target: { name } }) => {
		this.setState({ activeTab: name });
	};

	onActionFilterChange = e => {
		this.setState(
			{
				actionFilter: e?.value,
				applicantLogPageNumber: 1,
				applicantLog: [],
				filteredApplicantLog: [],
			},
			() => this.getApplicantHistory(),
		);
	};

	onOfficerFilterChange = e => {
		this.setState(
			{
				officerFilter: e?.value,
				applicantLogPageNumber: 1,
				applicantLog: [],
				filteredApplicantLog: [],
			},
			() => this.getApplicantHistory(),
		);
	};

	onPosFilterChange = e => {
		this.setState(
			{
				positionFilter: e?.value,
				applicantLogPageNumber: 1,
				applicantLog: [],
				filteredApplicantLog: [],
			},
			() => this.getApplicantHistory(),
		);
	};

	processAplicantsDetails = data => {
		if (data["Posting Details"] == null) {
			// eslint-disable-next-line react/jsx-no-useless-fragment
			return <></>;
		}
		const auxData = data["Posting Details"].replace("\\r\\n", "");
		const splitData = auxData
			.split('"')
			.filter(
				item => item !== ":" && item !== "," && item !== "" && item !== "{" && item !== "}",
			);
		const mainText = [];
		for (let i = 0; i < splitData.length; i += 2) {
			const tagValue = [splitData[i], splitData[i + 1]];
			const tag = mappedMetadata[tagValue[0]];
			const frag = (
				<div>
					- {tag} : {tagValue[1]}
				</div>
			);
			if (i === 1) {
				const auxTitle = <strong>Viewing group</strong>;
				mainText.push(auxTitle);
			}
			if (i === 4) {
				const auxTitle = <strong>Type of assignment</strong>;
				mainText.push(auxTitle);
			}
			if (i === 11) {
				const auxTitle = <strong>Other options</strong>;
				mainText.push(auxTitle);
			}
			mainText.push(frag);
		}
		return mainText;
	};

	render() {
		const { lang } = global;
		const { timeZoneId, show, user } = this.props;
		const {
			jobHistoryList,
			jobHistoryItem,
			isLoading,
			activeTab,
			actionFilter,
			officerFilter,
			positionFilter,
			actionList,
			officerList,
			positionList,
			applicantLogHasMore,
			filteredApplicantLog,
			filteredApplicantLogItem,
			sortAsc,
		} = this.state;

		return (
			<Modal
				id="job-history-modal"
				size="lg"
				show={show}
				onHide={() => this.handleOnClose()}
			>
				<Modal.Header closeButton>
					<Modal.Title>{lang.t("Job History")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div>
						<ul
							className="row nav tabs"
							role="presentation"
						>
							<li
								role="presentation"
								className={`col-xs-5 col-xs-offset-1 col-sm-3 col-sm-offset-3 col-md-2 col-md-offset-4${
									activeTab === "job-log" ? " active" : ""
								}`}
							>
								<button
									type="button"
									role="tab"
									name="job-log"
									className="btn-block"
									onClick={this.changeTab}
								>
									{lang.t("Job Log")}
								</button>
							</li>
							<li
								role="presentation"
								className={`col-xs-5 col-sm-3 col-md-2${activeTab === "applicant-log" ? " active" : ""}`}
							>
								<button
									type="button"
									role="tab"
									name="applicant-log"
									className="btn-block"
									onClick={this.changeTab}
								>
									{lang.t("Applicant Log")}
								</button>
							</li>
						</ul>
					</div>
					<If condition={activeTab === "job-log"}>
						<div className="job-history-table">
							<div className="history-table-header hidden-xs">
								<div className="row">
									<strong className="col-sm-2 col-md-3">
										{lang.t("Modification Date")}
									</strong>
									<strong className="col-sm-2">{lang.t("Changed By")}</strong>
									<strong className="col-sm-2">{lang.t("Action")}</strong>
									<strong className="col-sm-4 col-md-3">
										{lang.t("Changes In")}
									</strong>
								</div>
							</div>
							<div className="history-table-body">
								<Choose>
									<When condition={jobHistoryList.length > 0}>
										<For
											each="job"
											of={jobHistoryList}
											index="index"
										>
											<div
												className="job-history-item"
												key={`jobHistory_${job.order}`}
											>
												<div
													className={`collapsing-panel row${jobHistoryItem[job.order].showDetails ? " expanded" : ""}`}
												>
													<div className="col-xs-12 col-sm-2 col-md-3">
														{utils.formatDate(
															`${job?.DateChange}Z`,
															"en-US",
															timeZoneId,
															"numeric",
														)}
														&nbsp;
														<br className="hidden-xs" />
														{utils.formatTime(
															new Date(`${job?.DateChange}Z`),
															user.UserPreferences !==
																"useMilitaryTime",
															timeZoneId,
														)}
													</div>
													<div className="col-xs-12 col-sm-2">
														{job.User?.FullName}
													</div>
													<div className="col-xs-12 col-sm-2">
														{job?.Action}
													</div>
													<div className="col-xs-12 col-sm-4 col-md-3">
														{job.Model}
													</div>
													<If
														condition={
															job.Changes.length > 0 &&
															job.Action === "Modified"
														}
													>
														<div className="col-xs-12 col-sm-2">
															<button
																type="button"
																className="btn btn-link btn-block text-left"
																onClick={() =>
																	this.toggleShowDetails(
																		job.order,
																	)
																}
															>
																{lang.t("Details")}
																<i
																	title="View Details"
																	className={classNames(
																		"rkf chevron-icon",
																		{
																			"rkf-chevron-down":
																				!jobHistoryItem[
																					job.order
																				].showDetails,
																			"rkf-chevron-up":
																				jobHistoryItem[
																					job.order
																				].showDetails,
																		},
																	)}
																/>
															</button>
														</div>
														<If
															condition={
																jobHistoryItem[job.order]
																	.showDetails
															}
														>
															<div className="col-xs-12 expanded-header hidden-xs">
																<div className="row">
																	<div className="col-xs-12 col-sm-6">
																		{lang.t("Property")}
																	</div>
																	<div className="col-xs-12 col-sm-3">
																		{lang.t("Previous value")}
																	</div>
																	<div className="col-xs-12 col-sm-3">
																		{lang.t("Current Value")}
																	</div>
																</div>
																<div className="separator separator-solid" />
															</div>
															<For
																each="change"
																of={job.Changes}
																index="index"
															>
																<div
																	className="col-xs-10 expanded-details"
																	key={`changes_${change.PropertyName}_${index}`}
																>
																	<div className="row">
																		<div className="col-xs-12 col-sm-6">
																			<span className="visible-xs-inline">{`${lang.t("Property")}: `}</span>
																			{`${change.PropertyName === "" ? "Not Set" : change.PropertyName}`}
																		</div>
																		<div className="col-xs-12 col-sm-3">
																			<span className="visible-xs-inline">{`${lang.t("Previous")}: `}</span>
																			{`${change.PreviousValue === "" ? "Not Set" : this.formatJobHistoryValue(change.PreviousValue)}`}
																		</div>
																		<div className="col-xs-12 col-sm-3">
																			<span className="visible-xs-inline">{`${lang.t("Current")}: `}</span>
																			{`${change.CurrentValue === "" ? "Not Set" : this.formatJobHistoryValue(change.CurrentValue)}`}
																		</div>
																	</div>
																</div>
															</For>
														</If>
													</If>
												</div>
											</div>
										</For>
									</When>
									<Otherwise>
										<div className="col-xs-12 text-center text-muted display-empty">
											{lang.t("No job history to display")}
										</div>
									</Otherwise>
								</Choose>
								<Loader
									active={isLoading}
									size="small"
								/>
							</div>
						</div>
					</If>
					<If condition={activeTab === "applicant-log"}>
						<div className="row">
							<ReactSelect
								id="location-id"
								className="col-xs-4"
								value={officerFilter}
								name="officerFilterId"
								onChange={this.onOfficerFilterChange}
								options={officerList}
								isLoading={false}
								placeholder="Filter by officer"
								isClearable
							/>
							<ReactSelect
								id="location-id"
								className="col-xs-4"
								value={actionFilter}
								name="actionFilterId"
								onChange={this.onActionFilterChange}
								options={actionList}
								isLoading={false}
								placeholder="Filter by action"
								isClearable
							/>
							<ReactSelect
								id="location-id"
								className="col-xs-4"
								value={positionFilter}
								name="positionFilterId"
								onChange={this.onPosFilterChange}
								options={positionList}
								isLoading={false}
								placeholder="Filter by position"
								isClearable
							/>
						</div>
						<div className="job-history-table">
							<div className="history-table-header hidden-xs">
								<div className="row">
									<div
										className="col-sm-2 col-md-3"
										role="button"
										tabIndex="0"
										onClick={() => this.changeSort()}
										onKeyPress={({ key }) => {
											if (key === "Enter") this.changeSort();
										}}
									>
										<strong>{lang.t("Date")}</strong>
										<i
											className={`glyphicon glyphicon-triangle-${sortAsc ? "top" : "bottom"}`}
										/>
									</div>
								</div>
							</div>
							<div className="history-table-body">
								<Choose>
									<When condition={filteredApplicantLog.length > 0}>
										<For
											each="log"
											of={filteredApplicantLog}
											index="index"
										>
											<div
												className="job-history-item"
												key={`applicantHistory_${log.order}`}
											>
												<div
													className={`collapsing-panel row${filteredApplicantLogItem[log.order].showDetails ? " expanded" : ""}`}
												>
													<div className="col-xs-12 col-sm-3">
														{utils.formatTime(
															new Date(`${log?.CreateDateUTC}Z`),
															user.UserPreferences !==
																"useMilitaryTime",
															timeZoneId,
															true,
														)}
														&nbsp;
														{utils.formatDate(
															`${log?.CreateDateUTC}Z`,
															"en-US",
															timeZoneId,
															"numeric",
														)}
													</div>
													<div className="col-xs-12 col-sm-7">
														{log?.ApplicantLog}
													</div>
													<If
														condition={
															log.Metadata &&
															Object.keys(log.Metadata).length > 0
														}
													>
														<div className="col-xs-12 col-sm-2">
															<button
																type="button"
																className="btn btn-link btn-block text-left"
																onClick={() =>
																	this.toggleShowApplicantDetails(
																		log.order,
																	)
																}
															>
																{lang.t("Details")}
																<i
																	title="View Details"
																	className={classNames(
																		"rkf chevron-icon",
																		{
																			"rkf-chevron-down":
																				!filteredApplicantLogItem[
																					log.order
																				].showDetails,
																			"rkf-chevron-up":
																				filteredApplicantLogItem[
																					log.order
																				].showDetails,
																		},
																	)}
																/>
															</button>
														</div>
														<If
															condition={
																filteredApplicantLogItem[log.order]
																	.showDetails
															}
														>
															<For
																each="key"
																of={Object.keys(log.Metadata)}
																index="index"
															>
																<div
																	className="col-xs-12 expanded-details"
																	key={`metadata_${key}_${index}`}
																>
																	<div className="row">
																		<div className="col-xs-12 col-sm-8">
																			<span>
																				<strong>{`${key.replaceAll("_", " ")}: `}</strong>
																				{key !==
																					"Posting Details" &&
																				key !==
																					JURISDICTION_SERVICE_DATE_METADATA_KEY
																					? log.Metadata[
																							key
																						]
																					: ""}
																			</span>
																			{key ===
																			JURISDICTION_SERVICE_DATE_METADATA_KEY
																				? `
																						${utils.formatDate(`${log.Metadata[key]}`, "en-US", undefined, "numeric")}`
																				: this.processAplicantsDetails(
																						log.Metadata,
																					)}
																		</div>
																	</div>
																</div>
															</For>
														</If>
													</If>
												</div>
											</div>
										</For>
										<If condition={applicantLogHasMore}>
											<ScrollDetect
												className="text-center"
												onVisible={this.getApplicantHistory}
											>
												<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
											</ScrollDetect>
										</If>
									</When>
									<Otherwise>
										<div className="col-xs-12 text-center text-muted display-empty">
											{lang.t("No applicants history to display")}
										</div>
									</Otherwise>
								</Choose>
								<Loader
									active={isLoading}
									size="small"
								/>
							</div>
						</div>
					</If>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						id="close-history-modal"
						className="btn btn-default"
						onClick={() => this.handleOnClose()}
					>
						Close
					</button>
				</Modal.Footer>
			</Modal>
		);
	}
}

export default JobHistoryModal;

JobHistoryModal.propTypes = {
	show: PropTypes.bool,
	onClose: PropTypes.func,
	timeZoneId: PropTypes.string.isRequired,
	user: PropTypes.any.isRequired,
	jobEventId: PropTypes.number,
};

JobHistoryModal.defaultProps = {
	show: false,
	onClose: () => {},
	jobEventId: undefined,
};
